import {
  Provider,
  useGetCrmAgencyConnectionDetails,
  useGetCrmAgentDetails,
  useInvalidateUseGetCrmAgentDetails,
  useInvalidateUseGetCrmAgencyConnectionDetails,
  useUpdateCrmAgentDetails,
} from '@urbanx/agx.crm.client';
import {
  AgxBadge,
  AgxPanel,
  AgxButton,
  AgxSpacer,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2';
import { useEffect, useState } from 'react';
import { AgxFormTextField } from '@urbanx/agx.forms';
import { AgentCrmRegistrationForm } from './agentCrmRegistrationFormDefinition';
import * as styles from '../AddOrEditTeamMemberPage.css';

interface AgentCrmRegistrationProps {
  agencyId: string;
  agentId: string;
}

const providerNames: Record<Provider, string> = {
  [Provider.Rex]: 'Rex',
  [Provider.Agentbox]: 'Agentbox',
  [Provider.BoxAndDice]: 'Box and Dice',
  [Provider.Vault]: 'Vault',
};

const AgentCrmRegistration = ({ agencyId, agentId }: AgentCrmRegistrationProps) => {
  const { pushToast } = useToast();
  const {
    data: crmConnectionDetails,
    isPending: crmConnectionDetailsPending,
    isError: crmConnectionDetailsError,
  } = useGetCrmAgencyConnectionDetails(agencyId, Boolean(agencyId));
  const activeProvider = crmConnectionDetails?.activeProvider;
  const { data: registration, isPending: registrationPending } = useGetCrmAgentDetails(
    agencyId,
    agentId,
    Boolean(agencyId && agentId && activeProvider)
  );
  const [registeredEmailAddress, setRegisteredEmailAddress] = useState('');
  const invalidateRegistration = useInvalidateUseGetCrmAgentDetails();
  const invalidateConnection = useInvalidateUseGetCrmAgencyConnectionDetails();

  useEffect(() => {
    setRegisteredEmailAddress(registration?.externalUserEmailAddress ?? '');
  }, [registration?.externalUserEmailAddress]);

  const { updateCrmAgentDetails, updateCrmAgentDetailsPending } = useUpdateCrmAgentDetails({
    onSuccess: async (_, command) => {
      const savedEmailAddress = command.externalUserEmailAddress ?? '';
      setRegisteredEmailAddress(savedEmailAddress);
      await invalidateRegistration(agencyId, agentId);
      pushToast({
        type: 'success',
        message: command.externalUserEmailAddress
          ? `${activeProvider ? providerNames[activeProvider] : 'CRM'} registration connected`
          : `${activeProvider ? providerNames[activeProvider] : 'CRM'} registration disconnected`,
      });
    },
    onError: async (error) => {
      pushToast({
        type: 'error',
        message: error.message || 'Unable to update CRM registration.',
      });
      await invalidateConnection(agencyId);
      await invalidateRegistration(agencyId, agentId);
    },
  });

  if (crmConnectionDetailsError) {
    return (
      <AgxPanel header="Agent CRM Registration">
        <AgxTypography id="agent-crm-connection-error" type="body_ui" size="medium" role="alert">
          Unable to check CRM connection
        </AgxTypography>
      </AgxPanel>
    );
  }

  if (crmConnectionDetailsPending || (activeProvider && registrationPending)) {
    return (
      <AgxPanel header="Agent CRM Registration">
        <></>
      </AgxPanel>
    );
  }

  if (!activeProvider) {
    return (
      <AgxPanel header="Agent CRM Registration">
        <AgxTypography id="agent-crm-not-connected" type="body_ui" size="medium">
          No CRM is connected to the Agency
        </AgxTypography>
      </AgxPanel>
    );
  }

  return (
    <RegistrationEditor
      key={`${agencyId}:${agentId}:${activeProvider}:${registeredEmailAddress}`}
      provider={activeProvider}
      registeredEmailAddress={registeredEmailAddress}
      requestPending={updateCrmAgentDetailsPending}
      updateRegistration={(externalUserEmailAddress) =>
        updateCrmAgentDetails({ agencyId, agentId, externalUserEmailAddress })
      }
    />
  );
};

const RegistrationEditor = ({
  provider,
  registeredEmailAddress,
  requestPending,
  updateRegistration,
}: {
  provider: Provider;
  registeredEmailAddress: string;
  requestPending: boolean;
  updateRegistration: (email?: string) => Promise<unknown>;
}) => {
  const registered = Boolean(registeredEmailAddress);
  const form = AgentCrmRegistrationForm.useForm({
    defaultValues: { emailAddress: registeredEmailAddress },
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      if (registered || requestPending)
        return { status: 'failed', error: new Error('Registration is unavailable.') };
      try {
        await updateRegistration(parsedValues.emailAddress);
        return { status: 'accepted', baseline: parsedValues };
      } catch (error) {
        // The mutation refreshes authoritative connection state and presents the failure.
        return { status: 'failed', error };
      }
    },
  });

  return (
    <AgxPanel
      header="Agent CRM Registration"
      headerActions={
        <AgxBadge
          type="colourfill"
          size="sm"
          colour={registered ? 'green' : 'grey'}
          text={registered ? 'Registered' : 'Not registered'}
        />
      }
    >
      <AgentCrmRegistrationForm.Root form={form} className={styles.basicForm}>
        <AgentCrmRegistrationForm.Field
          name="emailAddress"
          Input={AgxFormTextField}
          type="email"
          label={`${providerNames[provider]} email address`}
          disabled={registered || requestPending}
          readonly={registered}
          stretch
          hideOptionalText
        />
        <AgxSpacer direction="horizontal" fill contentJustification="end">
          {registered ? (
            <AgxButton
              id="unregister-agent-crm"
              text={requestPending ? 'Unregistering...' : 'Unregister'}
              size="medium"
              variant="hollow"
              danger
              disabled={requestPending}
              onClick={() => {
                if (requestPending) return;
                // Failure feedback and authoritative refetch belong to the mutation.
                void updateRegistration().catch(() => undefined);
              }}
            />
          ) : (
            <AgxButton
              id="register-agent-crm"
              text={requestPending ? 'Registering...' : 'Register'}
              size="medium"
              variant="primary"
              disabled={requestPending || !form.isDirty || !form.canSubmit}
              onClick={() => void form.submit()}
            />
          )}
        </AgxSpacer>
      </AgentCrmRegistrationForm.Root>
    </AgxPanel>
  );
};

export default AgentCrmRegistration;
